// src/patcher.ts — clodex patch: first-class Claude Code binary patcher.
//
// Uses tweakcc's programmatic API (readContent/writeContent — an exact-pinned,
// declared dependency; no npx, no network) to extract the bundled JS from the
// Claude Code binary, applies the clodex patch sites in-process
// (see patch-transforms.ts), and repacks. Adds:
//  - auto-config: the patch map is built from clodex favorites + aliases,
//    context windows resolved from registry model metadata (never asked),
//  - auto-apply: no confirmation, concise summary,
//  - idempotence: a manifest (~/.clodex/patch-state.json) records the claude
//    version + config hash; unchanged config → fast no-op,
//  - re-patch: stale config/version → seed the candidate from the pristine
//    backup instead of the live binary, so a patch is never stacked on a patch,
//  - a content-addressed pristine backup per version+content
//    (~/.tweakcc/claude-<ver>-<sha>.orig, see patch-backup.ts) that is only ever
//    used once its provenance is established,
//  - atomic publication: the patch is built in a sibling temp dir and renamed
//    over the live binary only after it fully succeeds,
//  - a pid lock (~/.clodex/patch.lock) so concurrent launches cannot race.

import { createHash } from 'node:crypto';
import {
  chmodSync,
  copyFileSync,
  existsSync,
  mkdtempSync,
  mkdirSync,
  readFileSync,
  renameSync,
  rmSync,
  statSync,
  unlinkSync,
  writeFileSync,
  openSync,
  closeSync,
  realpathSync,
} from 'node:fs';
import { homedir } from 'node:os';
import { basename, dirname, join } from 'node:path';
import pc from 'picocolors';
import * as p from '@clack/prompts';
import { getAppHome } from './paths.js';
import {
  getPatchManifestPath,
  readPatchManifest,
  type PatchManifest,
} from './patch-manifest.js';
import { loadPreferences, savePreferences } from './config.js';
import {
  contextLimitsFrom,
  readContextStop,
  resolveContextStop,
  selectContextStop,
  type ContextStop,
} from './context-modes.js';
import { resolveContextWindow } from './context-window.js';
import {
  applyLocalPatches,
  inspectLocalPatchSource,
  type LocalPatchSource,
} from './local-patches.js';
import {
  builtInPatchProofsChanged,
  captureBuiltInPatchProofs,
  type BuiltInPatchProof,
} from './built-in-patch-proofs.js';
import { loadRegistry } from './registry/io.js';
import { projectProviderCachedModels } from './registry/materialize.js';
import { isRetainedOpenCodeGoProvider } from './registry/resolve-template.js';
import { findModelsDevModel } from './registry/models-dev.js';
import { findClaudeBinary, getClaudeVersionForBinary } from './launch.js';
import { resolveThroughNpmShims } from './npm-shim.js';
import {
  inspectClaudeNativeBinaryPlaceholder,
  type ClaudeNativePackageState,
} from './claude-native-placeholder.js';
import {
  resignMachOBinary,
  restoreEntryModuleName,
  shimEntryModuleName,
} from './bun-entry-module.js';
import {
  restoreBunCompiledPointer,
  shimBunCompiledPointer,
} from './bun-compiled-pointer.js';
import {
  applyBundleWritePlan,
  planBundleWrite,
  readClaudeBundle,
  splitBundleSource,
  writableModuleIndex,
  type ClaudeBundle,
} from './bun-bundle.js';
import {
  backupDir,
  collectPristineFacts,
  contentAddressedBackupPath,
  installProvenancePath,
  isPatchedClaudeSource,
  legacyBackupPath,
  looksLikeLegacyClodexPatch,
  planInspectedPristineSource,
  planPristineSource,
  planRestoreOnly,
  readInstallProvenance,
  recordBackupProvenance,
  sha256File,
  tweakccMirrorBackupPath,
  type PristineFacts,
  type PristinePlan,
  type ResolvedPristinePlan,
} from './patch-backup.js';
import type { Installation } from 'tweakcc';
import { httpProxyDisplayName, httpProxyModelId } from './http-proxy/routes.js';
import { stripOneMContextSuffix } from './context-model-id.js';
import { getPatchReasoningCapabilities } from './provider-factory.js';
import {
  describeModelAliasRejection,
  normalizeModelAliases,
  type ModelAliasRejection,
  type StoredModelAlias,
} from './model-aliases.js';
import {
  applyClodexPatches,
  formatPatchSiteLine,
  PatchApplyError,
  projectNativeEffort,
  PATCH_TRANSFORMS_VERSION,
  type PatchSiteResult,
  type PatchScriptModelConfig,
} from './patch-transforms.js';

// ── Manifest ────────────────────────────────────────────────────────────────

// Preserve the patcher's public surface while the manifest reader is shared
// with the lightweight clodex-claude wrapper. Writes remain owned here.
export { getPatchManifestPath, readPatchManifest };
export type { PatchManifest };

export function getPatchLockPath(): string {
  return join(getAppHome(), 'patch.lock');
}

function writePatchManifest(manifest: PatchManifest, path = getPatchManifestPath()): void {
  mkdirSync(getAppHome(), { recursive: true, mode: 0o700 });
  writeFileSync(path, `${JSON.stringify(manifest, null, 2)}\n`, { encoding: 'utf8', mode: 0o600 });
}

// ── Desired patch config (pure given inputs) ────────────────────────────────

export interface DesiredPatchConfig {
  config: PatchScriptModelConfig;
  /** Full metadata per patched id, for surfaces that need more than the patch bakes in. */
  metaById: Record<string, PatchModelMeta>;
  /** Model ids whose context window is unknown (defaulting to Claude Code's 200k). */
  unknownWindows: string[];
  /** Saved aliases excluded from the patch while remaining in configuration. */
  rejectedAliases: StoredModelAlias[];
  rejectedAliasRejections: ModelAliasRejection[];
}

/**
 * Resolved per-model metadata. The patch bakes in a subset; the rest describes how
 * the window was arrived at, which `models --json` reports and diagnostics rely on.
 */
export interface PatchModelMeta {
  providerId?: string;
  modelId?: string;
  /** Effective window, after the model's headroom percentage. */
  contextWindow?: number;
  /** Window before headroom was applied. */
  rawContextWindow?: number;
  /** Highest raw window the model accepts. */
  maxContextWindow?: number;
  effectiveContextPercent?: number;
  /** Which stop produced these numbers. */
  contextStop?: ContextStop;
  /** Largest output the model accepts, independent of the input window. */
  maxOutputTokens?: number;
  /** Input size above which the provider bills the whole request at a higher rate. */
  pricingBoundary?: number;
  /** Canonical label, e.g. `GPT-5.6 Sol (OpenAI (ChatGPT))`. */
  displayName?: string;
  effort?: {
    levels: string[];
    defaultLevel: string;
  };
}

/**
 * Build the patch model config from favorites + aliases.
 * Keys are the bare `clodex:<provider>:<model>` ids (no [1m] suffix — the
 * context patch and the suffix are mutually exclusive). When an entry has an
 * alias, that alias becomes the model's identity inside the patched binary.
 */
export function buildPatchModelConfig(
  favorites: Array<{ providerId: string; modelId: string }>,
  aliases: unknown,
  modelMetaFor: (providerId: string, modelId: string) => PatchModelMeta | undefined,
): DesiredPatchConfig {
  const config: PatchScriptModelConfig = {};
  const metaById: Record<string, PatchModelMeta> = {};
  const unknownWindows: string[] = [];
  const normalizedAliases = normalizeModelAliases(aliases);
  const favoriteTargets = new Set(
    favorites.map(favorite => `${favorite.providerId}:${favorite.modelId}`),
  );
  const targetRejections: ModelAliasRejection[] = normalizedAliases.accepted
    .filter(({ alias }) => (
      !favoriteTargets.has(`${alias.providerId}:${alias.modelId}`)
    ))
    .flatMap(({ sources }) => sources.map(source => ({
      alias: source,
      reason: 'target-not-favorite',
    })));
  const aliasByFavorite = new Map(
    normalizedAliases.aliases
      .filter(alias => favoriteTargets.has(`${alias.providerId}:${alias.modelId}`))
      .map(alias => [
        `${alias.providerId}:${alias.modelId}`,
        alias.name,
      ]),
  );

  for (const favorite of favorites) {
    const id = stripOneMContextSuffix(httpProxyModelId(favorite.providerId, favorite.modelId));
    if (config[id]) continue;
    const meta = modelMetaFor(favorite.providerId, favorite.modelId);
    const context = meta?.contextWindow;
    const alias = aliasByFavorite.get(`${favorite.providerId}:${favorite.modelId}`);
    const entry: PatchScriptModelConfig[string] = {};
    if (alias) entry.alias = alias;
    if (context === undefined || context <= 0) unknownWindows.push(id);
    else if (context !== 200_000) entry.context = context;
    const display = meta?.displayName?.trim();
    if (display) entry.display = display;
    const effort = projectNativeEffort(meta?.effort);
    if (effort) entry.effort = effort;
    config[id] = entry;
    if (meta) metaById[id] = meta;
  }
  return {
    config,
    metaById,
    unknownWindows,
    rejectedAliases: [
      ...normalizedAliases.rejected,
      ...targetRejections.map(rejection => rejection.alias),
    ],
    rejectedAliasRejections: [
      ...normalizedAliases.rejections,
      ...targetRejections,
    ],
  };
}

export function reportRejectedModelAliases(
  rejections: ModelAliasRejection[],
): void {
  for (const rejection of rejections) {
    p.log.warn(
      `Saved model alias ${JSON.stringify(rejection.alias.name)} was not patched — `
      + `${describeModelAliasRejection(rejection.reason)}. The saved entry was preserved.`,
    );
  }
}

/** Canonical (key-sorted) hash of the transform-set version and patch model config. */
export function computePatchConfigHash(
  config: PatchScriptModelConfig,
  transformsVersion = PATCH_TRANSFORMS_VERSION,
  localPatchIdentity?: string,
): string {
  const canonical = Object.keys(config).sort().map(key => {
    const entry = config[key]!;
    return [
      key,
      entry.alias ?? null,
      entry.context ?? null,
      entry.display ?? null,
      entry.effort?.levels ?? null,
      entry.effort?.defaultLevel ?? null,
    ];
  });
  const payload: unknown[] = [transformsVersion, canonical];
  if (localPatchIdentity !== undefined) {
    payload.push(['local-patches', localPatchIdentity]);
  }
  return createHash('sha256').update(JSON.stringify(payload)).digest('hex');
}

/** Read favorites + aliases + registry model metadata from disk (no network, no credentials). */
export interface DesiredPatchConfigOptions {
  /**
   * Honour a launch-scoped `--context` as well as the saved stop. Off for the patch
   * config, which describes a binary that outlives the process; on for read-only
   * surfaces that report what this run is actually using.
   */
  sessionStops?: boolean;
}

export function buildDesiredPatchConfig(
  options: DesiredPatchConfigOptions = {},
): DesiredPatchConfig {
  const prefs = loadPreferences();
  const favorites = prefs.favoriteModels ?? [];
  const aliases = prefs.modelAliases;
  const registry = loadRegistry();

  const providerById = new Map(registry.providers.map(provider => [provider.id, provider]));
  const projectedModelsByProvider = new Map<string, ReturnType<typeof projectProviderCachedModels>>();
  const meta = new Map<string, PatchModelMeta>();
  for (const provider of registry.providers) {
    const projectedModels = projectProviderCachedModels(provider);
    projectedModelsByProvider.set(provider.id, projectedModels);
    for (const model of projectedModels) {
      const npm = model.npm ?? provider.api.npm ?? '';
      const upstreamModelId = model.upstreamModelId ?? model.id;
      const modelsDev = findModelsDevModel(provider.id, model.id);
      const effort = getPatchReasoningCapabilities(npm, upstreamModelId, {
        providerId: provider.id,
        apiBaseUrl: model.apiUrl ?? provider.api.url,
        supportedParameters: model.supportedParameters,
        reasoning: model.reasoning ?? modelsDev?.reasoning,
        interleavedReasoningField:
          model.interleavedReasoningField ?? modelsDev?.interleaved?.field,
        compatibility: model.compatibility,
        upstreamModelId,
      });
      // A patched binary outlives the process that wrote it, so the patch config
      // reads saved stops only: folding a launch-scoped `--context` in would bake a
      // one-off choice and report every later launch as stale.
      const limits = contextLimitsFrom(model, resolveContextWindow(model.id));
      const stop = resolveContextStop(
        limits,
        options.sessionStops
          ? selectContextStop(provider.id, model.id, prefs.modelContextModes)
          : readContextStop(prefs.modelContextModes, provider.id, model.id) ?? 'standard',
      );
      meta.set(`${provider.id}:${model.id}`, {
        providerId: provider.id,
        modelId: model.id,
        contextWindow: stop.effective > 0 ? stop.effective : undefined,
        rawContextWindow: stop.raw > 0 ? stop.raw : undefined,
        maxContextWindow: limits.maxContextWindow,
        effectiveContextPercent: limits.effectiveContextPercent,
        contextStop: stop.stop,
        maxOutputTokens: model.maxOutputTokens,
        pricingBoundary: limits.pricingBoundary,
        // Same label `clodex server` prints at startup and `models --list` shows.
        displayName: httpProxyDisplayName(model, provider.name),
        effort: effort.mode === 'controllable'
          ? { levels: effort.levels, defaultLevel: effort.defaultLevel }
          : undefined,
      });
    }
  }

  const projectedFavorites = favorites.filter(favorite => {
    const provider = providerById.get(favorite.providerId);
    if (!provider || !isRetainedOpenCodeGoProvider(provider)) return true;
    return projectedModelsByProvider.get(provider.id)?.some(model => model.id === favorite.modelId) ?? false;
  });

  return buildPatchModelConfig(
    projectedFavorites,
    aliases,
    (providerId, modelId) => meta.get(`${providerId}:${modelId}`),
  );
}

// ── Staleness (pure) ────────────────────────────────────────────────────────

export type PatchState = 'unpatched' | 'current' | 'stale-config' | 'stale-binary';

export function evaluatePatchState(
  manifest: PatchManifest | null,
  current: { binaryPath: string; claudeVersion: string; configHash: string; binarySize?: number },
): PatchState {
  if (!manifest) return 'unpatched';
  if (manifest.binaryPath !== current.binaryPath) return 'unpatched';
  if (manifest.claudeVersion !== current.claudeVersion) return 'stale-binary';
  if (current.binarySize !== undefined && manifest.patchedSize !== current.binarySize) return 'stale-binary';
  if (manifest.configHash !== current.configHash) return 'stale-config';
  return 'current';
}

// ── Lock (pid + staleness) ──────────────────────────────────────────────────

const PATCH_LOCK_STALE_MS = 10 * 60 * 1000;

interface PatchLockContent {
  pid: number;
  startedAt: number;
}

function pidIsAlive(pid: number): boolean {
  try {
    process.kill(pid, 0);
    return true;
  } catch (err) {
    return (err as NodeJS.ErrnoException).code === 'EPERM';
  }
}

/**
 * Try to take the patch lock. Returns a release function, or null when another
 * live process holds it. A lock left by a dead pid or older than 10 minutes is
 * treated as stale and replaced.
 */
export function tryAcquirePatchLock(
  lockPath = getPatchLockPath(),
  opts: { now?: number; isAlive?: (pid: number) => boolean } = {},
): (() => void) | null {
  const now = opts.now ?? Date.now();
  const isAlive = opts.isAlive ?? pidIsAlive;
  mkdirSync(join(lockPath, '..'), { recursive: true, mode: 0o700 });

  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const fd = openSync(lockPath, 'wx');
      const content: PatchLockContent = { pid: process.pid, startedAt: now };
      writeFileSync(fd, JSON.stringify(content));
      closeSync(fd);
      return () => {
        try {
          unlinkSync(lockPath);
        } catch {
          // already gone
        }
      };
    } catch {
      // Lock exists — check staleness.
      let stale = false;
      try {
        const existing = JSON.parse(readFileSync(lockPath, 'utf8')) as PatchLockContent;
        stale = !existing.pid
          || !isAlive(existing.pid)
          || (typeof existing.startedAt === 'number' && now - existing.startedAt > PATCH_LOCK_STALE_MS);
      } catch {
        stale = true; // unreadable lock file → stale
      }
      if (!stale) return null;
      try {
        unlinkSync(lockPath);
      } catch {
        // raced with the owner's cleanup — retry loop handles it
      }
    }
  }
  return null;
}

// ── Binary + backup helpers ─────────────────────────────────────────────────

/** Outcome of locating the binary `clodex patch` should operate on. */
export type ClaudePatchTarget =
  | { ok: true; binaryPath: string; version: string }
  | { ok: false; reason: 'binary-not-found' }
  | {
      ok: false;
      /**
       * TWEAKCC_CC_INSTALLATION_PATH names a file that is not there. Distinct from
       * `binary-not-found` so the message can say which variable to fix rather
       * than report that no Claude Code was found while one is installed.
       */
      reason: 'patch-target-missing';
      declaredPath: string;
    }
  | { ok: false; reason: 'version-unknown'; binaryPath: string }
  | {
      ok: false;
      reason: 'native-binary-missing';
      binaryPath: string;
      nativePackageState: ClaudeNativePackageState;
      installScriptPath: string | null;
    }
  | {
      ok: false;
      reason: 'launcher-unresolved';
      /**
       * The program the launcher names when that could be read — so `--restore`
       * can still match a manifest recorded against it — and the launcher
       * itself when it could not. `declaredTarget` says which of the two this
       * is, because the difference decides what `--restore` may attempt.
       */
      binaryPath: string;
      shimPath: string;
      declaredTarget: string | null;
      detail: string;
    };

/**
 * Locate the REAL native binary, bypassing wrapper shims (e.g. cmux) that a
 * plain PATH lookup can return. Order (ported from the relay-ai wrapper):
 * TWEAKCC_CC_INSTALLATION_PATH → ~/.local/bin/claude (stable native-install
 * symlink) → findClaudeBinary() PATH lookup.
 *
 * **The patch target override is TWEAKCC_CC_INSTALLATION_PATH, not
 * CLODEX_CLAUDE_PATH.** The latter governs which claude gets LAUNCHED and is only
 * reached here through `findClaudeBinary()`, i.e. last — deliberately. A user
 * whose CLODEX_CLAUDE_PATH points at a wrapper shim needs that for launching, and
 * honouring it here patches the wrapper instead of Claude Code: `resolveThroughNpmShims`
 * follows npm launchers, not arbitrary wrappers, so the wrapper is what would be
 * handed to tweakcc — issue #193's "Unable to detect installation type", with a
 * shim's older version selecting the wrong pristine backup on top (the version
 * note below). Issue #217 asked for it to be honoured; that is why it is not.
 * What #217 was right about is that the behaviour was undocumented and that the
 * remedy printed for an unfollowable launcher named the wrong variable — both
 * fixed, and `clodex patch` now says when it is ignoring a CLODEX_CLAUDE_PATH.
 *
 * `%USERPROFILE%\.local\bin\claude.exe`, which the Windows native installer
 * writes, is still NOT probed here. Adding it as a fallback made a restore
 * weakness reachable: `findClaudeBinary()` returns the same null for
 * "CLODEX_CLAUDE_PATH names a file that is gone" as for "nothing was found", so
 * a stale explicit override silently became that other install, and `--restore`
 * copied the missing install's pristine bytes over it and dropped the manifest.
 * Reproduced on real 2.1.266 binaries. `--restore` now refuses when the manifest
 * records another install rather than selecting a backup by version tag (issue
 * #199), so this fallback can land — but as its own change with its own Windows
 * evidence, and only once the no-manifest case is covered too.
 *
 * Whatever that finds is then followed through any npm launcher script to the
 * program it starts (see `npm-shim.ts`). `findBinaryOnPath` prefers
 * `claude.cmd` on Windows ON PURPOSE — launching claude there needs a shell
 * script — but that file is a launcher, not Claude Code, and handing it to the
 * patcher produced issue #193's "Unable to detect installation type from path"
 * on every npm-installed Windows machine. Following it here, rather than in
 * `findClaudeBinary`, keeps the launch path untouched.
 *
 * The version is probed from THAT binary, never from whatever `claude` PATH
 * resolves to. The two chains diverge exactly when the overrides matter (a shim
 * on PATH, a different install under the override), and the version selects the
 * pristine backup that seeds the patch candidate — so a version borrowed from
 * another install publishes those other bytes over the user's Claude Code. There
 * is no fallback version for the same reason: an unprobeable binary is an error.
 */
/**
 * Set by the last `resolveClaudeBinaryForPatch` when CLODEX_CLAUDE_PATH was set and
 * something else decided the patch target. Reported by the command, so the warning
 * lands next to the target it chose rather than from inside a pure resolver.
 */
let ignoredLaunchOverride: { used: string; ignored: string } | null = null;

/** The ignored CLODEX_CLAUDE_PATH the last resolve saw, if any. Clears on read. */
export function takeIgnoredLaunchOverride(): { used: string; ignored: string } | null {
  const ignored = ignoredLaunchOverride;
  ignoredLaunchOverride = null;
  return ignored;
}

export function resolveClaudeBinaryForPatch(): ClaudePatchTarget {
  const envOverride = process.env['TWEAKCC_CC_INSTALLATION_PATH']?.trim() || null;
  const nativeSymlink = join(homedir(), '.local', 'bin', 'claude');
  if (envOverride && !existsSync(envOverride)) {
    return { ok: false, reason: 'patch-target-missing', declaredPath: envOverride };
  }
  const source = envOverride
    || (existsSync(nativeSymlink) ? nativeSymlink : null)
    || findClaudeBinary();
  if (!source) return { ok: false, reason: 'binary-not-found' };
  let resolved: string;
  try {
    resolved = realpathSync(source);
  } catch {
    return { ok: false, reason: 'binary-not-found' };
  }
  // Before anything is copied, backed up or renamed: an unfollowable launcher is
  // a hard stop, not a file to patch. Everything downstream — the candidate
  // directory, the pristine backup, the manifest, the final rename and
  // `--restore` — keys off the single path returned here.
  const followed = resolveThroughNpmShims(resolved);
  if (!followed.ok) {
    return {
      ok: false,
      reason: 'launcher-unresolved',
      binaryPath: followed.declaredTarget ?? followed.shimPath,
      shimPath: followed.shimPath,
      declaredTarget: followed.declaredTarget,
      detail: followed.detail,
    };
  }
  resolved = followed.path;
  // A CLODEX_CLAUDE_PATH that is set but did not decide this is worth one line: it
  // is documented as overriding discovery, and for the patch target it does not.
  // Compare the fully resolved program, so naming the install by its symlink or by
  // the launcher in front of it counts as naming it and says nothing.
  const launchOverride = process.env['CLODEX_CLAUDE_PATH']?.trim() || null;
  let launchOverrideResolved: string | null = null;
  if (launchOverride) {
    try {
      const followedOverride = resolveThroughNpmShims(realpathSync(launchOverride));
      launchOverrideResolved = followedOverride.ok ? followedOverride.path : realpathSync(launchOverride);
    } catch {
      launchOverrideResolved = launchOverride;
    }
  }
  ignoredLaunchOverride = launchOverrideResolved && launchOverrideResolved !== resolved
    ? { used: resolved, ignored: launchOverride as string }
    : null;
  try {
    if (!statSync(resolved).isFile()) return { ok: false, reason: 'binary-not-found' };
  } catch {
    return { ok: false, reason: 'binary-not-found' };
  }
  const placeholder = inspectClaudeNativeBinaryPlaceholder(resolved);
  if (placeholder) {
    return {
      ok: false,
      reason: 'native-binary-missing',
      binaryPath: resolved,
      ...placeholder,
    };
  }
  const version = getClaudeVersionForBinary(resolved);
  if (!version) return { ok: false, reason: 'version-unknown', binaryPath: resolved };
  return { ok: true, binaryPath: resolved, version };
}

type PatchTargetCommand = 'patch' | 'restore' | 'launch';

function installScriptCommand(path: string | null): string {
  return path === null
    ? '`node node_modules/@anthropic-ai/claude-code/install.cjs` '
      + '(adjust the path for a local or global install)'
    : `\`node "${path}"\``;
}

/** Accurate, actionable message per failure reason — the reasons are NOT interchangeable. */
export function describePatchTargetFailure(
  target: Extract<ClaudePatchTarget, { ok: false }>,
  command: PatchTargetCommand = 'patch',
): string {
  if (target.reason === 'launcher-unresolved') {
    return `${target.shimPath} starts Claude Code but is not Claude Code itself, and clodex could `
      + `not follow it: ${target.detail}. clodex will not patch a launcher script — that fails with `
      + '"Unable to detect installation type". Set TWEAKCC_CC_INSTALLATION_PATH to the Claude Code '
      + 'program itself (for an npm install on Windows that is '
      + 'node_modules\\@anthropic-ai\\claude-code\\bin\\claude.exe under the directory holding the '
      + 'launcher), then run the command again.';
  }
  if (target.reason === 'native-binary-missing') {
    const installer = installScriptCommand(target.installScriptPath);
    const remedy = target.nativePackageState === 'present'
      ? `The platform-native package is installed, so run ${installer} to finish the installation.`
      : target.nativePackageState === 'missing'
        ? 'The platform-native optional package is missing, so `install.cjs` cannot repair this '
          + 'state. Reinstall Claude Code without `--ignore-scripts` / `--omit=optional`.'
        : 'clodex could not determine whether the platform-native package is installed. If it is, '
          + `run ${installer}; otherwise reinstall Claude Code without \`--ignore-scripts\` / `
          + '`--omit=optional`.';
    const retry = command === 'restore'
      ? 'Then run `clodex patch --restore` again.'
      : command === 'launch'
        ? 'Then run the command again.'
        : 'Then run `clodex patch` again.';
    const restore = command === 'restore'
      ? ` If you need to restore by hand, pristine backups are in ${backupDir()}.`
      : '';
    return `${target.binaryPath} is Claude Code's npm placeholder, not its native binary, so the `
      + `npm install is incomplete. ${remedy} ${retry}${restore} If this is a custom wrapper `
      + 'rather than the npm placeholder, set TWEAKCC_CC_INSTALLATION_PATH to the native Claude '
      + 'Code binary.';
  }
  if (target.reason === 'patch-target-missing') {
    return `TWEAKCC_CC_INSTALLATION_PATH is set to ${target.declaredPath}, which does not exist. `
      + 'clodex will not look for another Claude Code instead — patching or restoring a different '
      + 'install than the one you named is how one install\'s pristine bytes end up over another\'s. '
      + 'Point it at the Claude Code program, or unset it to let clodex find your install.';
  }
  return target.reason === 'binary-not-found'
    ? 'claude binary not found. Install Claude Code or set TWEAKCC_CC_INSTALLATION_PATH.'
    : `Could not determine the version of ${target.binaryPath} (\`claude --version\` failed). `
      + 'clodex will not patch a binary whose version it cannot read, because the version selects the '
      + 'pristine backup it patches from. If a previous patch left the install broken, '
      + '`clodex patch --restore` still works — it reads the version from the patch manifest.';
}

// ── Patch reporting ─────────────────────────────────────────────────────────

/** Per-site report lines + summary, same shape the old tweakcc output showed. */
export function summarizePatchResults(results: PatchSiteResult[]): string[] {
  const lines = results.map(formatPatchSiteLine);
  const ok = results.filter(r => r.status === 'OK').length;
  const skip = results.filter(r => r.status === 'SKIP').length;
  const failed = results.filter(r => r.status === 'FAIL');
  lines.push(`clodex patch: ${ok} applied, ${skip} skipped, ${failed.length} failed`);
  if (failed.length) {
    lines.push(`clodex patch: FAILED patches: ${failed.map(f => f.name).join('; ')}`);
  }
  return lines;
}

// ── Apply / restore ─────────────────────────────────────────────────────────

export interface ApplyOutcome {
  ok: boolean;
  message: string;
  detailLines?: string[];
}

/**
 * Establish that a backup really holds the pristine bytes of `version` before
 * anything is built from it. Content-addressed backups are already self-validated
 * by name; a legacy `claude-<ver>.orig` carries no hash, so the only evidence
 * that its bytes are that version is running it.
 *
 * Both callers put these bytes on the user's install — `applyPatch` seeds the
 * patch candidate from them and renames it into place, `--restore` copies them
 * over the binary directly — so an unverified backup is a silent downgrade
 * either way.
 */
function verifyPristineSource(
  plan: Extract<PristinePlan, { action: 'restore' }>,
  version: string,
): { ok: true } | { ok: false; message: string } {
  if (!plan.probeVersion) return { ok: true };
  const backupVersion = getClaudeVersionForBinary(plan.backupPath);
  if (backupVersion === version) return { ok: true };
  return {
    ok: false,
    message: `Refusing to use ${plan.backupPath} as the pristine source for claude ${version}: it reports `
      + `${backupVersion ? `version ${backupVersion}` : 'no version at all'}. `
      + 'That backup predates content-addressed backup names and does not hold this version\'s bytes. '
      + 'Remove it (or reinstall Claude Code), then run `clodex patch`.',
  };
}

/**
 * Publish a file into the backup directory atomically: write a temp file beside
 * the destination, then rename it into place.
 *
 * A plain `copyFileSync` of a ~250 MB binary that is interrupted (Ctrl-C, crash,
 * ENOSPC) leaves a TRUNCATED file under a name asserting its content hash. That
 * is the one corruption content-addressing cannot notice without re-hashing, and
 * it made `clodex patch` fail permanently with a misleading extraction error.
 * `rename` within one directory is atomic, so a backup file is either absent or
 * complete — never half-written.
 */
/**
 * Copy `from` over `to` by writing a temp file beside it and renaming, so `to` is
 * replaced as a whole new inode rather than rewritten underneath anything holding
 * it. Used for backup files, and for the live binary on `--restore`.
 *
 * `mode` is applied to the temp file before the rename. `copyFileSync` takes the
 * SOURCE's mode, not the destination's, so a rename without this would publish a
 * binary carrying whatever permissions the backup file happened to have.
 */
export function publishFileByRename(from: string, to: string, mode?: number): void {
  const temp = `${to}.tmp-${process.pid}-${Date.now().toString(36)}`;
  try {
    copyFileSync(from, temp);
    if (mode !== undefined) chmodSync(temp, mode);
    renameSync(temp, to);
  } catch (err) {
    try {
      rmSync(temp, { force: true });
    } catch {
      // best effort — the temp name is unique and does not match the scanner's
      // `claude-*.orig` pattern, so a leftover is never mistaken for a backup.
    }
    throw err;
  }
}

/**
 * Refuse bytes that already carry a clodex patch. `planInspectedPristineSource`
 * applies this rule to the LIVE binary; this is the same rule for bytes that came
 * out of a BACKUP, which `verifyPristineSource` cannot cover because it only
 * proves the version — and a patched claude reports its own version perfectly.
 */
function describePoisonedPristineSource(backupPath: string, version: string): string {
  return `Refusing to patch from ${backupPath}: those bytes already carry a clodex patch, `
    + 'so they are not pristine and patching them would stack a patch on a patch. '
    + `That backup was recorded as pristine for claude ${version} by an older clodex, which `
    + 'snapshotted whatever binary was live when no backup existed. '
    + `Delete ${backupPath}, reinstall Claude Code, then run \`clodex patch\`.`;
}

function requiredEffortPatchFailures(results: PatchSiteResult[]): PatchSiteResult[] {
  return results.filter(result =>
    result.status === 'FAIL'
    && (
      result.name.startsWith('PATCH 8a:')
      || result.name.startsWith('PATCH 8b:')
      || result.name.startsWith('PATCH 8c:')
      || result.name.startsWith('PATCH 9:')
    ),
  );
}

function builtInPatchVerificationFailed(
  source: string,
  config: PatchScriptModelConfig,
  expected: PatchSiteResult[],
  proofs: BuiltInPatchProof[],
): boolean {
  if (builtInPatchProofsChanged(source, proofs)) return true;

  let verification: ReturnType<typeof applyClodexPatches>;
  try {
    verification = applyClodexPatches(source, config);
  } catch {
    return true;
  }
  if (verification.content !== source || verification.results.length !== expected.length) {
    return true;
  }

  const expectedByName = new Map(expected.map(result => [
    result.name.replace(/ \(refresh\)$/, ''),
    result,
  ]));
  for (const result of verification.results) {
    const original = expectedByName.get(result.name.replace(/ \(refresh\)$/, ''));
    if (!original) return true;
    if (original.status === 'FAIL') {
      if (result.status !== 'FAIL' || result.extra !== original.extra) return true;
    } else if (result.status !== 'SKIP') {
      return true;
    }
  }
  return false;
}

function discardLocalPatchOutcome(
  builtInContent: string,
  results: PatchSiteResult[],
): { content: string; results: PatchSiteResult[] } {
  return {
    content: builtInContent,
    results: [
      ...results.map(result => result.status === 'OK'
        ? {
            status: 'SKIP' as const,
            name: result.name,
            extra: 'rolled back after built-in verification failed',
          }
        : result),
      {
        status: 'FAIL',
        name: 'LOCAL PATCH SET',
        extra: 'local output changed built-in patch sites',
      },
    ],
  };
}

export async function applyPatch(
  binaryPath: string,
  version: string,
  desired: DesiredPatchConfig,
  configHash: string,
  opts: {
    trace: boolean;
    manifest: PatchManifest | null;
    localPatches?: LocalPatchSource;
  },
): Promise<ApplyOutcome> {
  let candidateDir: string | undefined;
  let results: PatchSiteResult[];
  let patchedSize: number;
  let patchedSha256: string;
  let backup: string;
  let pristineSha256: string;
  /** What tied `backup` to this install, carried into the manifest. */
  const pristine: { provenance: 'established' | 'assumed' } = { provenance: 'established' };
  try {
    mkdirSync(backupDir(), { recursive: true });

    // tweakcc's lib entry pulls in its interactive-picker deps (ink/react), so
    // load it lazily — only when a patch is actually applied.
    const { tryDetectInstallation, readContent, writeContent } = await import('tweakcc');

    // Everything below reads and writes the CANDIDATE; the live binary is only
    // touched by the final rename. Seeding it is also how the source gets
    // extracted, so no path pays for the (expensive, ~250 MB) extraction twice
    // unless the seed itself has to change.
    candidateDir = mkdtempSync(join(dirname(binaryPath), '.clodex-patch-'));
    const candidatePath = join(candidateDir, basename(binaryPath));
    const seedCandidate = async (
      from: string,
    ): Promise<{ installation: Installation; source: string; bundle: ClaudeBundle | null }> => {
      copyFileSync(from, candidatePath);
      // Claude Code 2.1.229 renamed the module tweakcc looks for; the shim is a
      // same-length rename that only has to be in place while tweakcc reads. It
      // is undone immediately so the seeded candidate stays byte-identical to
      // `from` — the snapshot path publishes these exact bytes as the pristine
      // backup under a content address, so re-signing here (which would swap
      // Claude Code's signature for an ad-hoc one) must not happen.
      const shim = shimEntryModuleName(candidatePath);
      const installation = await tryDetectInstallation({ path: candidatePath });
      // Claude Code 2.1.242 split the bundle across ~1,370 modules, and tweakcc's
      // `readContent` returns only the one it recognizes by name — since that
      // release, a stub holding none of the code clodex patches. Read every
      // JavaScript module instead and hand the transforms all of it; fall back to
      // tweakcc when the blob cannot be parsed, which is exactly what clodex did
      // before code splitting existed.
      const bundle = readClaudeBundle(candidatePath);
      // Say so when the fallback fires on a native binary. Otherwise a bundle clodex declined to
      // read looks EXACTLY like a drifted anchor: the single-module read returns the entry, which
      // on 2.1.242 and later is a stub, so the patch fails at its first required site and the
      // report names that site and nothing else. (An npm install is plain JavaScript with no Bun
      // blob in it, so its fallback is the normal path and says nothing.)
      if (!bundle && installation.kind === 'native') {
        p.log.warn(
          `Could not read ${candidatePath} as a Bun module table, so only the module tweakcc names `
          + 'is being patched. On Claude Code 2.1.242 and later that module is a stub holding none '
          + 'of the code clodex patches, and the patch below will fail at its first required '
          + 'site — the cause is this read, not a changed anchor.',
        );
      }
      const source = bundle ? bundle.source : await readContent(installation);
      if (shim) restoreEntryModuleName(candidatePath, shim, { resign: false });
      return { installation, source, bundle };
    };

    const facts: PristineFacts = collectPristineFacts({
      version,
      binaryPath,
      manifest: opts.manifest,
    });

    const initial = planPristineSource(facts);
    let loaded: { installation: Installation; source: string; bundle: ClaudeBundle | null } | null = null;
    let plan: ResolvedPristinePlan;
    if (initial.action === 'inspect') {
      // Unrecognized bytes. The only reliable "is this already patched?" signal
      // lives in the extracted JS (the native binary compresses it, so a raw
      // byte scan finds nothing), so seed the candidate from the LIVE binary and
      // inspect that copy — byte-identical, so it answers the question about the
      // live binary, which is what the R1 guard below turns on.
      loaded = await seedCandidate(binaryPath);
      if (looksLikeLegacyClodexPatch(loaded.source)) {
        p.log.warn(
          `${binaryPath} carries no clodex patch marker but does look like a patch from a clodex `
          + 'older than the effort patch sites. Treating it as pristine; if `/model` shows stale '
          + 'entries afterwards, reinstall Claude Code and run `clodex patch` again.',
        );
      }
      plan = planInspectedPristineSource(facts, { patched: isPatchedClaudeSource(loaded.source) });
      // Only an unpatched live binary may be both snapshotted AND patched from.
      // Any other verdict means these bytes are not pristine: drop them and
      // re-seed from an established backup below.
      if (plan.action !== 'snapshot') loaded = null;
    } else {
      plan = initial;
    }

    if (plan.action === 'error') return { ok: false, message: plan.message };
    for (const note of plan.notes) p.log.warn(note);

    if (plan.action === 'restore') {
      const verified = verifyPristineSource(plan, version);
      if (!verified.ok) return { ok: false, message: verified.message };
      p.log.info(`Binary differs from its pristine backup — building a fresh patch candidate from ${plan.backupPath}.`);
    }
    pristineSha256 = plan.pristineSha256;
    backup = plan.backupPath;

    // Seed from the established backup on the `reuse` and `restore` paths
    // (`snapshot` already inspected the live binary's own bytes), then hold EVERY
    // path to the same rule: the bytes about to be patched must carry no clodex
    // patch marker. `verifyPristineSource` above only proves the version, and a
    // patched claude reports its version perfectly well.
    //
    // Nothing has been written to the backup directory yet, deliberately. A
    // poisoned backup must not be laundered into a content-addressed name (which
    // later runs then trust without a probe), and must not clobber the tweakcc
    // mirror on its way to failing.
    // A guessed association is recorded AS a guess rather than skipped: the record
    // is what stops a later run from promoting it (the live bytes now match the
    // backup because the guess put them there, and the manifest this run writes was
    // itself derived from it). It never selects and never refuses.
    //
    // Every plan INHERITS the confidence already recorded for these bytes. `reuse`
    // matches bytes a guess may have put there; canonicalizing a legacy backup would
    // otherwise launder a guess through a filename change; and a `snapshot` is no
    // exception either, even though it inspected the live bytes itself — if a guess
    // restored those very bytes onto this install, "the install holds them" is a fact
    // the guess created, so establishing on it would hand these bytes' true owner a
    // refusal. Nothing promotes a guess. The protection a promotion was supposed to
    // buy is already provided by refusing the fallback for an install these bytes were
    // guessed onto.
    //
    // Confidence belongs to the CONTENT, not to a filename. Asking only about the name
    // this plan chose left a third name carrying the guess: restore B from a legacy
    // backup by version tag, patch A so the legacy file is adopted under its content
    // address, then patch B — which picks the canonical name, finds no record of B
    // beside it, and established what the legacy name still called a guess. So every
    // alias of these exact bytes is consulted, and the two names a record can sit
    // beside while its backup is gone (the content address and the legacy name) are
    // read directly, because the scan only finds records next to an existing `.orig`.
    const inheritsAGuess = (path: string): boolean => {
      const existing = readInstallProvenance(installProvenancePath(path, binaryPath));
      return existing === 'damaged' || (existing !== null && existing.assumed);
    };
    const provenanceAssumed = (plan.action === 'restore' && plan.assumedForThisInstall)
      || inheritsAGuess(plan.backupPath)
      || inheritsAGuess(contentAddressedBackupPath(version, plan.pristineSha256))
      || inheritsAGuess(legacyBackupPath(version))
      || facts.backups.some(
        candidate => candidate.sha256 === plan.pristineSha256
          && candidate.assumedInstalls.includes(binaryPath),
      );
    // From what the write LEFT on disk, not from what it asked for. The two agree
    // today — an established request promotes, so the writer never answers `assumed`
    // to one — but the manifest is the next run's evidence, and reading it from the
    // result rather than the intent means that stays true without depending on the
    // writer's promotion rule.
    const recordProvenance = (path: string) => {
      if (recordBackupProvenance(path, binaryPath, { assumed: provenanceAssumed }) === 'assumed') {
        pristine.provenance = 'assumed';
      }
    };

    if (!loaded) {
      loaded = await seedCandidate(backup);
      if (isPatchedClaudeSource(loaded.source)) {
        return { ok: false, message: describePoisonedPristineSource(backup, version) };
      }
    } else if (plan.action === 'snapshot') {
      // Bootstrap, from the candidate rather than re-reading the live binary:
      // these are the exact bytes just inspected and found unpatched, and
      // `writeContent` has not run yet. Sourcing the backup from them makes
      // name/content/inspected-bytes agreement structural instead of incidental.
      // Written unconditionally: this branch is only reached when no VALID backup
      // holds these bytes, so an existing file at this content address is a
      // corrupt one being replaced by the bytes its name asserts.
      //
      // The candidate has been read from and written to by now (the entry-module shim, at least),
      // so prove it still IS those bytes rather than assuming it. `plan.pristineSha256` is the
      // live binary's hash and the name this is about to be filed under, so a mismatch would mean
      // publishing a backup that lies about its own contents — which only a much later run would
      // notice, as a backup nobody can trust.
      if (sha256File(candidatePath) !== plan.pristineSha256) {
        throw new Error(
          `the patch candidate no longer matches the pristine bytes it was seeded from; refusing `
          + `to publish it as ${plan.backupPath}`,
        );
      }
      // Record BEFORE the backup becomes visible. A published `.orig` with no record
      // beside it is exactly the unattributed same-version file this exists to
      // prevent, and a crash or a full disk between the two writes would leave one
      // for good. The reverse order is safe: a record whose backup never appeared is
      // inert, because scanning starts from the `.orig` files.
      recordProvenance(plan.backupPath);
      publishFileByRename(candidatePath, plan.backupPath);
    }

    // Adopt a legacy `claude-<ver>.orig` under its content address so later runs
    // can self-validate it instead of re-running the version probe. The original
    // file is left alone — `tweakcc --restore` and older clodex still find it.
    //
    // Whether the canonical name already holds the right bytes is decided from
    // CONTENT, not existence: `facts.backups` carries the hash the scan already
    // computed, and anything at that name the scan rejected (truncated, foreign)
    // is absent from it and gets replaced rather than adopted and published.
    const canonical = contentAddressedBackupPath(version, pristineSha256);
    if (canonical !== backup) {
      // Both names first, for the same reason the snapshot path records before it
      // publishes: whichever file exists must already say whose bytes it holds.
      recordProvenance(backup);
      recordProvenance(canonical);
      const alreadyStored = facts.backups.some(
        candidate => candidate.path === canonical && candidate.sha256 === pristineSha256,
      );
      if (!alreadyStored) publishFileByRename(backup, canonical);
      backup = canonical;
    }

    // Mirror the pristine copy to tweakcc's restore location (always from the
    // backup, never the live binary — so it stays pristine after patching).
    publishFileByRename(backup, tweakccMirrorBackupPath());

    // Record which install these bytes are the pristine content of, beside the
    // backup itself. The manifest written at the end of this function says the same
    // thing, but it holds one install and `--restore` deletes it, so this is what
    // still answers "whose bytes are these?" on a later run — and refuses to hand
    // them to a different install (issue #204). Both names are recorded when a
    // legacy backup was adopted under its content address: the legacy file is left
    // on disk deliberately, for `tweakcc --restore` and older clodex, so leaving it
    // unattributed would leave the one file this change cannot speak for.
    // Idempotent, so repeating the snapshot path's write costs nothing.
    recordProvenance(backup);
    if (plan.backupPath !== backup) recordProvenance(plan.backupPath);

    const builtIn = applyClodexPatches(loaded.source, desired.config);
    results = builtIn.results;
    const failedEffortPatches = requiredEffortPatchFailures(results);
    if (failedEffortPatches.length > 0) {
      throw new PatchApplyError(
        `clodex patch: required effort patches failed: ${
          failedEffortPatches.map(result => result.name).join('; ')
        }`,
        results,
      );
    }
    let builtInProofs: BuiltInPatchProof[] = [];
    let local: { content: string; results: PatchSiteResult[] } | undefined;
    if (opts.localPatches?.enabled && typeof opts.localPatches.source === 'string') {
      try {
        builtInProofs = captureBuiltInPatchProofs(
          builtIn.content,
          desired.config,
          builtIn.results,
        );
      } catch {
        local = {
          content: builtIn.content,
          results: [{
            status: 'FAIL',
            name: 'LOCAL PATCH SET',
            extra: 'could not capture built-in postconditions',
          }],
        };
      }
    }
    local ??= opts.localPatches
      ? await applyLocalPatches(builtIn.content, opts.localPatches)
      : { content: builtIn.content, results: [] };
    if (
      local.results.some(result => result.status === 'OK')
      && builtInPatchVerificationFailed(
        local.content,
        desired.config,
        builtIn.results,
        builtInProofs,
      )
    ) {
      local = discardLocalPatchOutcome(builtIn.content, local.results);
    }
    results = [...results, ...local.results];
    // Repacking reads the module list back off the candidate, so the shim has to
    // be in place again — and undone again before the candidate is published,
    // because Claude Code's sibling native modules resolve against the entry
    // module's own path.
    const writeShim = shimEntryModuleName(candidatePath);
    let publishedBlob = false;
    if (loaded.bundle) {
      // One repack, however many modules the patch touched — and the repack is used only to RESIZE
      // the binary's Bun section. What it rebuilds is thrown away: Bun's blob carries structures no
      // module struct points at (see `bun-bundle.ts`), so the published blob is the pristine one
      // with every patched source appended past the end of it.
      const writable = writableModuleIndex(candidatePath);
      if (writable === null) {
        throw new Error('no module of the patch candidate carries a name tweakcc can write to');
      }
      const plan = planBundleWrite(
        candidatePath,
        loaded.bundle,
        splitBundleSource(loaded.bundle, local.content),
        writable,
      );
      // Claude Code 2.1.257 moved the global Bun reads its blob's address from off the 16 KiB
      // boundary tweakcc's ELF repack scans, so the repack could not find it and threw on all
      // four ELF builds. A no-op everywhere else. See `bun-compiled-pointer.ts`.
      const bunPointerShim = shimBunCompiledPointer(candidatePath);
      await writeContent(loaded.installation, plan.content);
      if (bunPointerShim) restoreBunCompiledPointer(candidatePath, bunPointerShim);
      applyBundleWritePlan(candidatePath, plan);
      publishedBlob = true;
    } else {
      const bunPointerShim = shimBunCompiledPointer(candidatePath);
      await writeContent(loaded.installation, local.content);
      if (bunPointerShim) restoreBunCompiledPointer(candidatePath, bunPointerShim);
    }
    if (writeShim) restoreEntryModuleName(candidatePath, writeShim, { resign: true });
    // The repack signs on its way out, so publishing the blob after it leaves an invalid signature.
    // Restoring the entry-module name re-signs already; this covers the binary that needed no shim.
    else if (publishedBlob) resignMachOBinary(candidatePath);
    patchedSize = statSync(candidatePath).size;
    patchedSha256 = sha256File(candidatePath);
    renameSync(candidatePath, binaryPath);
  } catch (err) {
    const detailLines = err instanceof PatchApplyError ? summarizePatchResults(err.results) : [];
    if (opts.trace && detailLines.length) {
      process.stderr.write(`${detailLines.join('\n')}\n`);
    }
    return {
      ok: false,
      message: `Patch failed: ${err instanceof Error ? err.message : String(err)}`,
      detailLines,
    };
  } finally {
    if (candidateDir !== undefined) {
      try {
        rmSync(candidateDir, { recursive: true, force: true });
      } catch (err) {
        if (opts.trace) {
          process.stderr.write(
            `clodex patch: could not remove temporary candidate directory: ${
              err instanceof Error ? err.message : String(err)
            }\n`,
          );
        }
      }
    }
  }
  if (opts.trace) {
    process.stderr.write(`${summarizePatchResults(results).join('\n')}\n`);
  }

  const manifest: PatchManifest = {
    binaryPath,
    claudeVersion: version,
    configHash,
    patchedSize,
    patchedSha256,
    backupPath: backup,
    pristineSha256,
    ...(pristine.provenance === 'assumed' ? { pristineProvenance: 'assumed' as const } : {}),
    patchedAt: new Date().toISOString(),
  };
  writePatchManifest(manifest);

  const modelCount = Object.keys(desired.config).length;
  const aliasCount = Object.values(desired.config).filter(entry => entry.alias).length;
  const windowCount = Object.values(desired.config).filter(entry => entry.context).length;
  return {
    ok: true,
    message: `Patched claude ${version}: ${modelCount} model${modelCount === 1 ? '' : 's'}, `
      + `${aliasCount} alias${aliasCount === 1 ? '' : 'es'}, ${windowCount} context window${windowCount === 1 ? '' : 's'}.`,
    detailLines: summarizePatchResults(results),
  };
}

/**
 * `clodex patch --restore` — put the pristine bytes back over the live binary.
 *
 * A pristine backup exists PRECISELY for the case where a patched install is broken,
 * so generic recovery must not require the broken binary to run. When `--version`
 * cannot be probed, the version comes from the manifest instead — it recorded
 * `claudeVersion`, `backupPath` and `pristineSha256` when the binary was patched,
 * which establishes provenance without executing anything.
 *
 * The manifest fallback is sound only while clodex was the last writer of the
 * unprobeable target — the bad-patch recovery case. An npm placeholder proves a
 * package manager replaced those bytes, so that old manifest is no longer
 * authoritative for this path even though the wrapper package version is readable.
 * That state refuses above; generic unprobeable binaries retain manifest recovery.
 */
function runRestoreCommand(target: ClaudePatchTarget): number {
  if (!target.ok && (
    target.reason === 'binary-not-found'
    // A named path that is not there names no program to restore over, and looking
    // for another install is exactly what must not happen here.
    || target.reason === 'patch-target-missing'
    || target.reason === 'native-binary-missing'
  )) {
    p.log.error(describePatchTargetFailure(target, 'restore'));
    return 1;
  }
  // A launcher clodex could not read names no program, so there is no path to
  // look up in the manifest and nothing safe to restore over. Falling through
  // would report a failed `claude --version` — the wrong cause, pointing the
  // user at the wrong remedy — for a refusal that happens before any probe.
  if (!target.ok && target.reason === 'launcher-unresolved' && target.declaredTarget === null) {
    p.log.error(describePatchTargetFailure(target, 'restore'));
    return 1;
  }
  const binaryPath = target.binaryPath;
  const manifest = readPatchManifest();

  let version: string;
  if (target.ok) {
    version = target.version;
  } else if (manifest?.binaryPath === binaryPath && manifest.claudeVersion) {
    version = manifest.claudeVersion;
    p.log.warn(
      `Could not read the version of ${binaryPath} (\`claude --version\` failed) — using claude `
      + `${version} from the patch manifest, which recorded it when this binary was patched. `
      + 'This is expected when a bad patch left the install unable to run.',
    );
  } else {
    p.log.error(
      `Could not determine the version of ${binaryPath} (\`claude --version\` failed), and `
      + 'no patch manifest records a pristine backup for it, so clodex cannot tell which backup '
      + `belongs to this install. Restore it by hand from ${backupDir()} (or reinstall Claude Code).`,
    );
    return 1;
  }

  // Publish the same way the patch path does. Rewriting a binary IN PLACE leaves
  // it on the same inode, and macOS caches a code signature per vnode for a
  // binary that has been executed — an in-place overwrite invalidates the pages
  // under that cache and has been reported to leave every later launch killed
  // with `Code Signature Invalid` (issue #216) until the file was replaced
  // through a new inode. The earlier reasoning here was that a restore has
  // "nothing to publish atomically", which is true and beside the point: what
  // matters is inode identity, not atomicity. This path still carries the full
  // provenance check.
  const plan = planRestoreOnly(collectPristineFacts({ version, binaryPath, manifest }));
  if (plan.action === 'error') {
    p.log.error(plan.message);
    return 1;
  }
  for (const note of plan.notes) p.log.warn(note);
  const verified = verifyPristineSource(plan, version);
  if (!verified.ok) {
    p.log.error(verified.message);
    return 1;
  }
  // Record what this restore knows BEFORE the binary changes and before the manifest
  // is cleared.
  //
  // Before the copy, because a version-tag guess changes the live bytes to the
  // backup's: once that has happened, "the live bytes match this backup" is no
  // longer independent evidence, so the record saying it was a guess has to already
  // be on disk. If it cannot be, the guess is refused — it is the optional
  // compatibility path, and skipping it costs the user nothing but a message.
  //
  // Before the manifest is cleared, because the manifest may be the ONLY thing tying
  // that backup to this install — an upgrading user's backup predates these records
  // — and clearing it without writing one leaves the backup unattributed for good,
  // which is the state issue #204 turns destructive on a second install.
  // A manifest for this same path but a DIFFERENT claude version is about an older
  // install of Claude Code whose backup is still on disk, and clearing it below would
  // leave that backup unattributed. `clodex patch` migrates the same case.
  if (manifest && manifest.backupPath && manifest.binaryPath === binaryPath
    && manifest.claudeVersion !== version && manifest.backupPath !== plan.backupPath) {
    try {
      if (existsSync(manifest.backupPath)) {
        recordBackupProvenance(manifest.backupPath, manifest.binaryPath, {
          assumed: manifest.pristineProvenance === 'assumed',
        });
      }
    } catch (err) {
      p.log.warn(
        `Could not record in ${backupDir()} that ${manifest.backupPath} holds the pristine bytes of `
        + `claude ${manifest.claudeVersion} at ${manifest.binaryPath} `
        + `(${err instanceof Error ? err.message : String(err)}). That version may need to be `
        + 'reinstalled rather than restored.',
      );
    }
  }

  let recorded: 'established' | 'assumed' | 'failed';
  try {
    recorded = recordBackupProvenance(plan.backupPath, binaryPath, { assumed: plan.assumedForThisInstall });
  } catch (err) {
    recorded = 'failed';
    const detail = err instanceof Error ? err.message : String(err);
    if (plan.assumedForThisInstall) {
      p.log.error(
        `Refusing to restore ${binaryPath} from ${plan.backupPath}: nothing but the claude `
        + `${version} version tag ties those bytes to this install, and clodex cannot record that in `
        + `${backupDir()} (${detail}). Writing them without that record would leave the machine unable `
        + 'to tell afterwards that the match was a guess. Fix the backup directory, or reinstall '
        + 'Claude Code to make this install pristine.',
      );
      return 1;
    }
    p.log.warn(
      `Could not record in ${backupDir()} that ${plan.backupPath} holds the pristine bytes of `
      + `${binaryPath} (${detail}). Restoring anyway and keeping the patch manifest, so that record `
      + 'is not lost — a later restore would otherwise have nothing tying that backup to this install.',
    );
  }

  // Keep the live binary's own permissions: `copyFileSync` would hand it the
  // backup file's instead, and a non-executable claude is a worse outcome than
  // the one being fixed.
  let publishedMode: number | undefined;
  try {
    publishedMode = statSync(binaryPath).mode;
  } catch {
    // The target is gone between the plan and the write; let the rename create it
    // with the backup's mode rather than refuse a rescue this late.
  }
  try {
    publishFileByRename(plan.backupPath, binaryPath, publishedMode);
  } catch (err) {
    // A rename can fail where the old in-place copy would have worked — Windows
    // refuses to replace a file another process holds open, and a temp file beside
    // the binary needs a writable directory. This is a rescue command, so fall back
    // to the in-place write rather than leave a broken install unrestored; the user
    // is told, because on macOS that write is the fault this path exists to avoid.
    p.log.warn(
      `Could not replace ${binaryPath} through a new file (${err instanceof Error ? err.message : String(err)}); `
      + 'writing the pristine bytes in place instead. If claude then fails to start with a code-signing '
      + 'error, copy the backup to a new file and move it over the binary by hand.',
    );
    copyFileSync(plan.backupPath, binaryPath);
  }

  // The manifest records ONE install, and clearing it is meant to say "this install
  // is no longer patched". Two things must hold first. Only this install's own
  // record may be cleared: a provenance record lets a restore succeed while the
  // manifest still holds a DIFFERENT install (both are recorded, so each can be
  // restored), and deleting the manifest there would throw away the other install's
  // only rescue record — the damage issue #199 named, arrived at from the other
  // direction. And an ESTABLISHED record must now stand in its place: a manifest is
  // stronger evidence than a guess, so dropping it in exchange for one destroys
  // testimony rather than migrating it.
  if (recorded === 'established' && (!manifest || manifest.binaryPath === binaryPath)) {
    try {
      unlinkSync(getPatchManifestPath());
    } catch {
      // no manifest to remove
    }
  }
  p.log.success(`Restored pristine claude ${version} from ${plan.backupPath}.`);
  return 0;
}

export async function runPatchCommand(opts: {
  restore?: boolean;
  trace?: boolean;
  localPatches?: boolean;
} = {}): Promise<number> {
  const target = resolveClaudeBinaryForPatch();
  const ignoredOverride = takeIgnoredLaunchOverride();
  if (ignoredOverride) {
    p.log.warn(
      `CLODEX_CLAUDE_PATH is set to ${ignoredOverride.ignored}, but it does not choose what gets `
      + `patched — ${ignoredOverride.used} does. CLODEX_CLAUDE_PATH selects the claude that gets `
      + 'LAUNCHED; set TWEAKCC_CC_INSTALLATION_PATH to patch a specific install.',
    );
  }

  // Handled BEFORE the patch path's version check, because `--restore` has its
  // own rules: it must still work on a binary that no longer runs.
  if (opts.restore) return runRestoreCommand(target);

  if (!target.ok) {
    p.log.error(describePatchTargetFailure(target));
    return 1;
  }
  const { binaryPath, version } = target;

  if (opts.localPatches !== undefined) {
    savePreferences({ localPatchesEnabled: opts.localPatches });
  }

  const desired = buildDesiredPatchConfig();
  if (Object.keys(desired.config).length === 0) {
    p.log.error('No favorite models to patch. Save favorites with `clodex models` first.');
    return 1;
  }
  for (const id of desired.unknownWindows) {
    p.log.warn(`No context window metadata for ${id} — Claude Code will assume the 200k default.`);
  }
  reportRejectedModelAliases(desired.rejectedAliasRejections);

  const localPatches = inspectLocalPatchSource(
    loadPreferences().localPatchesEnabled === true,
  );
  const configHash = computePatchConfigHash(
    desired.config,
    PATCH_TRANSFORMS_VERSION,
    localPatches.enabled ? localPatches.configIdentity : undefined,
  );
  const manifest = readPatchManifest();
  const state = evaluatePatchState(manifest, {
    binaryPath,
    claudeVersion: version,
    configHash,
    binarySize: statSync(binaryPath).size,
  });

  if (state === 'current') {
    p.log.success(`claude ${version} is already patched with the current model config — nothing to do.`);
    return 0;
  }

  const release = tryAcquirePatchLock();
  if (!release) {
    p.log.warn('Another clodex process is patching the claude binary right now — skipped.');
    return 1;
  }

  // This run is about to REPLACE the manifest, which holds one install. When the one
  // it holds is a different install (or this install at a different claude version),
  // that manifest is the only thing attributing its backup — an upgrading user's
  // backup predates the per-install records — so migrate its testimony first or
  // patching one install silently strips the other's rescue record (issue #204).
  if (manifest && manifest.backupPath
    && (manifest.binaryPath !== binaryPath || manifest.claudeVersion !== version)) {
    try {
      if (existsSync(manifest.backupPath)) {
        recordBackupProvenance(manifest.backupPath, manifest.binaryPath, {
          assumed: manifest.pristineProvenance === 'assumed',
        });
      }
    } catch (err) {
      p.log.warn(
        `Could not record in ${backupDir()} that ${manifest.backupPath} holds the pristine bytes of `
        + `${manifest.binaryPath} before replacing the patch manifest `
        + `(${err instanceof Error ? err.message : String(err)}). That install may need to be `
        + 'reinstalled rather than restored.',
      );
    }
  }

  try {
    // Never patch on top of a patch: applyPatch decides from the pristine
    // backups for THIS version (and the manifest) whether the live binary is
    // already patched, and seeds the candidate from established pristine bytes
    // rather than from the live binary whenever it is not.
    const outcome = await applyPatch(binaryPath, version, desired, configHash, {
      trace: opts.trace ?? false,
      manifest,
      localPatches,
    });
    if (!outcome.ok) {
      p.log.error(outcome.message);
      for (const line of outcome.detailLines ?? []) p.log.info(pc.dim(line));
      return 1;
    }
    p.log.success(outcome.message);
    if (!opts.trace) {
      for (const line of outcome.detailLines ?? []) p.log.info(pc.dim(line));
    }
    return 0;
  } finally {
    release();
  }
}

// ── Launch-time check ───────────────────────────────────────────────────────

/**
 * Cheap patch-state probe for `clodex claude`:
 *  - TTY: offer to patch (y/N); declining continues the launch.
 *  - non-TTY (or agent stdout mode): one-line notice, never prompt, never block.
 *  - concurrent launches: the lock loser prints a notice and continues.
 */
export async function runLaunchPatchCheck(opts: { agentStdout?: boolean; dryRun?: boolean } = {}): Promise<void> {
  try {
    const desired = buildDesiredPatchConfig();
    if (Object.keys(desired.config).length === 0) return; // nothing to patch

    const target = resolveClaudeBinaryForPatch();
    if (!target.ok) {
      // A patch check must never break a launch. "Not found" is silent (the user
      // may not use the patcher at all); every other resolution failure is worth
      // one dim line, since each also blocks `clodex patch`.
      if (target.reason !== 'binary-not-found' && !opts.agentStdout) {
        console.error(pc.dim(`clodex: ${describePatchTargetFailure(target, 'launch')}`));
      }
      return;
    }
    const resolved = target;

    const localPatches = inspectLocalPatchSource(
      loadPreferences().localPatchesEnabled === true,
    );
    const configHash = computePatchConfigHash(
      desired.config,
      PATCH_TRANSFORMS_VERSION,
      localPatches.enabled ? localPatches.configIdentity : undefined,
    );
    const manifest = readPatchManifest();
    const state = evaluatePatchState(manifest, {
      binaryPath: resolved.binaryPath,
      claudeVersion: resolved.version,
      configHash,
      binarySize: statSync(resolved.binaryPath).size,
    });
    if (state === 'current') return;

    const interactive = !opts.dryRun && !opts.agentStdout
      && process.stdin.isTTY === true && process.stdout.isTTY === true;
    if (!interactive) {
      if (!opts.agentStdout) {
        console.error(pc.dim(`clodex: claude binary is ${state === 'unpatched' ? 'not patched' : 'stale-patched'} for your favorites — run \`clodex patch\`.`));
      }
      return;
    }

    const answer = await p.confirm({
      message: state === 'unpatched'
        ? 'Claude Code is not patched for your clodex favorites. Patch now?'
        : 'The Claude Code patch is stale (config or claude version changed). Re-patch now?',
      initialValue: false,
    });
    if (p.isCancel(answer) || answer !== true) return;

    await runPatchCommand({});
  } catch (err) {
    // The patch check must never block a launch.
    console.error(pc.dim(`clodex: patch check skipped (${err instanceof Error ? err.message : String(err)})`));
  }
}
